<template>
  <!-- The API ad: two Prefetch pitches that take turns — the brand-data API,
       then the MCP server — with a countdown ring in the corner. Layout-free so
       it can sit in the generator's content column (ApiCtaCard) or straight
       inside a brand, hub or blog page's container.

       The rotation holds while the pointer is over the card, while keyboard
       focus is inside it, while it is off screen or the tab is in the
       background, and for good once the visitor uses the snippet — nobody
       should have the code they're reading swapped out from under them.

       Along the top runs a faint band of animated brackets
       (atoms/BracketField.vue), after the promo cards on Supabase's event
       pages. It fades out within a few rows, so it only shows behind the top
       of the copy and, on a wide card, in the strip above the code window. -->
  <BaseCard
    ref="root"
    as="section"
    variant="frame"
    class="relative overflow-hidden"
    aria-roledescription="carousel"
    aria-label="Prefetch API"
    @pointerenter="onPointer($event, true)"
    @pointerleave="onPointer($event, false)"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
  >
    <BracketField class="absolute inset-x-0 top-0 h-16 sm:h-20" fade="down" :ink-alpha="0.06" :peak-alpha="0.24" interactive />

    <!-- Both slides share one grid cell, so the card is always as tall as the
         taller one; the slides lay out by the card's width (PromoSlide.vue). -->
    <div class="relative grid [container-type:inline-size] *:[grid-area:1/1]">
      <PromoSlide
        :active="current === 0"
        :position="`1 of ${slides.length}`"
        eyebrow="For developers"
        title="Build with brand data"
        :heading-tag="headingTag"
        cta-label="Get started free"
        :cta-href="apiUrl('api_card', route.path)"
        @cta="ctaClicked('brand_data')"
      >
        Fetch logos, colors, fonts and company details for any website in one API call, the same API that powers
        colorize.design.
        <template #visual>
          <BrandDataPreview />
        </template>
      </PromoSlide>

      <PromoSlide
        :active="current === 1"
        :position="`2 of ${slides.length}`"
        eyebrow="For AI agents"
        title="Brand data, right in your agent"
        :heading-tag="headingTag"
        cta-label="Get your API key"
        :cta-href="apiUrl('mcp_card', route.path)"
        @cta="ctaClicked('mcp')"
      >
        Connect to the MCP server and Claude, Codex or Cursor can pull logos, colors and fonts for any site on their
        own. No code to write.
        <template #visual>
          <McpSetupSnippet @engage="stopped = true" />
        </template>
        <template #secondary>
          <TextLink :href="mcpDocsUrl('mcp_card', route.path)" class="text-sm" @click="docsClicked">
            Setup guide
            <template #iconAfter>
              <ArrowUpRightIcon class="h-4 w-4" aria-hidden="true" />
            </template>
          </TextLink>
        </template>
      </PromoSlide>
    </div>

    <CountdownRing
      class="absolute right-3 top-3 sm:right-4 sm:top-4"
      :duration="slides[current].duration"
      :paused="paused"
      :cycle="cycle"
      :label="`Show ${slides[next()].name}`"
      @done="show(next())"
      @click="show(next())"
    />
  </BaseCard>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { ArrowUpRightIcon } from '@heroicons/vue/16/solid'
import BaseCard from '~/components/atoms/BaseCard.vue'
import BracketField from '~/components/atoms/BracketField.vue'
import TextLink from '~/components/atoms/TextLink.vue'
import CountdownRing from '~/components/atoms/CountdownRing.vue'
import PromoSlide from '~/components/molecules/PromoSlide.vue'
import BrandDataPreview from '~/components/molecules/BrandDataPreview.vue'
import McpSetupSnippet from '~/components/molecules/McpSetupSnippet.vue'
import { trackEvent } from '~/composables/analytics'
import { apiUrl, mcpDocsUrl } from '~/utils/site'

defineProps({
  // The generator's own <h1> sits at the top of the page, so the ad's headings
  // are <h2>s everywhere.
  headingTag: { type: String, default: 'h2' },
})

// The snippet needs more reading time than a headline, so it stays up longer.
const slides = [
  { id: 'brand_data', name: 'brand data API', duration: 8000 },
  { id: 'mcp', name: 'MCP server', duration: 14000 },
]

const current = ref(0)
// Bumped on every change of slide, which restarts the ring's fill.
const cycle = ref(0)
const next = () => (current.value + 1) % slides.length
const show = (index) => {
  current.value = index
  cycle.value += 1
}

const hovered = ref(false)
const focused = ref(false)
// Starts false, so the prerendered ring is paused and doesn't count down
// before the visitor has scrolled the card into view.
const inView = ref(false)
const pageHidden = ref(false)
const stopped = ref(false)
const paused = computed(() => stopped.value || hovered.value || focused.value || !inView.value || pageHidden.value)

// Mouse only: a tap fires pointerenter too, and on touch there's no
// pointerleave until the next tap elsewhere, so the card would stay paused.
const onPointer = (event, over) => {
  if (event.pointerType === 'mouse') hovered.value = over
}
// Keyboard focus only (`:focus-visible`). A mouse click on the ring focuses it
// as well, and would otherwise keep the card paused after the pointer left.
const onFocusIn = (event) => {
  focused.value = event.target.matches?.(':focus-visible') ?? false
}
const onFocusOut = (event) => {
  if (!root.value?.$el.contains(event.relatedTarget)) focused.value = false
}

const root = ref(null)
let observer = null
const onVisibility = () => {
  pageHidden.value = document.visibilityState === 'hidden'
}

onMounted(() => {
  onVisibility()
  document.addEventListener('visibilitychange', onVisibility)
  observer = new IntersectionObserver(([entry]) => { inView.value = entry.isIntersecting }, { threshold: 0.5 })
  observer.observe(root.value.$el)
})
onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', onVisibility)
  observer?.disconnect()
})

// The card sits on the generator, the brand hub, every brand page and the
// blog, so events carry where they were clicked from. The signup event keeps
// its original name, so the total stays comparable with what has already been
// collected; `ad` says which of the two pitches earned it. The links carry the
// page in `utm_term`, so a signup can be traced back to it on Prefetch.
const route = useRoute()

const ctaClicked = (ad) => {
  try {
    trackEvent('Signup API Clicked', { page: route.path, ad })
  } catch {}
}

const docsClicked = () => {
  try {
    trackEvent('MCP Docs Clicked', { page: route.path })
  } catch {}
}
</script>
